// Order selection and PDF memory limits are deliberately independent.
export const maxBulkOrderLabelOrders = 250;
export const bulkOrderLabelPdfChunkSize = 100;

export function pendingBulkOrderLabelDrafts<T extends { orderId: string }>(
  drafts: T[],
  results: Array<{ ok?: boolean; orderId?: string }>
) {
  const completedOrderIds = new Set(
    results
      .filter((result) => result.ok && result.orderId)
      .map((result) => String(result.orderId).trim())
      .filter(Boolean)
  );
  return drafts.filter((draft) => !completedOrderIds.has(String(draft.orderId).trim()));
}

export function bulkOrderLabelPdfChunks(shipmentIds: string[]) {
  const ids = Array.from(new Set(shipmentIds.map((id) => id.trim()).filter(Boolean)));
  if (ids.length > maxBulkOrderLabelOrders) throw new Error("Bulk order label selection exceeds the safe limit.");
  const chunks: string[][] = [];
  for (let index = 0; index < ids.length; index += bulkOrderLabelPdfChunkSize) {
    chunks.push(ids.slice(index, index + bulkOrderLabelPdfChunkSize));
  }
  return chunks;
}

export async function downloadBulkOrderLabelPdfBatches(
  shipmentIds: string[],
  download: (ids: string[], part: number, parts: number, onProgress: (completed: number) => void) => Promise<{ downloadedShipmentIds: string[]; failedShipmentIds: string[] }>,
  onProgress?: (completed: number) => void
) {
  const chunks = bulkOrderLabelPdfChunks(shipmentIds);
  const downloadedShipmentIds: string[] = [];
  for (let index = 0; index < chunks.length; index += 1) {
    const offset = downloadedShipmentIds.length;
    const result = await download(chunks[index], index + 1, chunks.length, (completed) => onProgress?.(offset + completed));
    if (result.failedShipmentIds.length || result.downloadedShipmentIds.length !== chunks[index].length ||
        chunks[index].some((id) => !result.downloadedShipmentIds.includes(id))) {
      throw new Error("Incomplete bulk order label PDF.");
    }
    downloadedShipmentIds.push(...result.downloadedShipmentIds);
  }
  return { downloadedShipmentIds, failedShipmentIds: [] as string[], pdfCount: chunks.length };
}
